<template>
  <section class="page-create-package">
    <page-header :title="title"></page-header>

    <!-- Loading Section -->
    <LoadingOverlay :active="isLoading" />

    <main class="page-content">
      <div class="wrapper">
        <package-status-card
          class="mb-4"
          :isAvailableForSale.sync="isAvailableForSale"
          :isVisibleOnApp.sync="isVisibleOnApp"
        ></package-status-card>
        <package-editor
          ref="packageEditor"
          v-model="form"
          class="mb-4"
          :image-url="imageUrl"
          :thumbnail-url="thumbnailUrl"
          :thumbnail-ad-url="thumbnailAdUrl"
          :targets="targets"
          :seller-trade-parties="sellerTradeParties"
          :package-groups="packageGroups"
          :collections="collections"
          :payment-campaigns="paymentCampaigns"
          :language="localization.TH"
          @selectedService="handleSetupPricings"
        ></package-editor>
        <div class="text-right">
          <sc-button variant="primary" @click="handleClickSave"
            >บันทึก</sc-button
          >
        </div>
      </div>
    </main>
  </section>
</template>

<script>
import PageHeader from "@/lib/components/PageHeader";
import ScButton from "@/lib/components/ScButton";
import { baanRueJaiClient, backendAdminClient } from "@/dependencies";
import { uploadPublicImage } from "@/god";
import { isNil, isEmpty, head, tail } from "ramda";
import { toOptionalDateTime, toOptionalISOString } from "@/lib/dateTime";
import { toCamelCaseObject } from "@/lib/util";
import {
  convertStangToBathAndRoundTwoDecimal,
  convertBathToStangAndRoundTwoDecimal,
} from "@/lib/formatter";
import LoadingOverlay from "@/lib/components/LoadingOverlay";
import { localization } from "@/lib/mapper";
import PackageStatusCard from "../components/PackageStatusCard";
import PackageEditor from "../components/PackageEditor";
import { findEmptyAppointmentPriceRow } from "../models/PackageOptionPrice";

export default {
  components: {
    PageHeader,
    ScButton,
    PackageEditor,
    PackageStatusCard,
    LoadingOverlay,
  },
  data() {
    const form = {
      name: "",
      receiptName: "",
      target: "สำหรับบ้าน",
      price: 0,
      pricings: [],
      billingCycle: 1,
      detail: "",
      sellerTradePartyId: null,
      termsAndConditions: "",
      services: [],
      projects: [],
      homes: [],
      imageFile: null,
      thumbnailFile: null,
      thumbnailAdFile: null,
      detailImagesPosition: "TOP",
      detailImageAlbums: [],
      isSecret: true,
      isExpireAfterAppointment: false,
      noteLabel: null,
      registrationForm: null,
      hasRegistrationForm: false,
      hasOptionPackage: false,
      packageCollection: null,
      purchasingLimit: null,
      originalPrice: null,
      purchaseSuccessTitle: null,
      purchaseSuccessMessage: null,
      expiredAt: null,
      packageOptions: null,
      minimumQuantity: 1,
      maximumQuantity: null,
      newServicePrices: [],
      newOptionVariantPrices: [],
      isReadOnlyOptionPrice: true,
      packageAlbums: [],
      coinAmountPercent: null,
      coinAmountFix: null,
      hasAdvanceNotice: false,
      advanceNoticeDuration: null,
      advanceNoticeMessage: null,
      isIgnoreCountCoupon: false,
      paymentCampaignIds: [],
      isRequiredIssueReceipt: true,
      labelIds: [],
    };

    const imageUrl = null;
    const thumbnailAdUrl = null;
    const thumbnailUrl = null;
    const targets = [];
    const sellerTradeParties = [];
    const paymentCampaigns = [];
    const packageGroups = [];
    const collections = [];
    const title = "เพิ่ม Package";

    return {
      form,
      isAvailableForSale: false,
      isVisibleOnApp: false,
      imageUrl,
      thumbnailAdUrl,
      thumbnailUrl,
      targets,
      sellerTradeParties,
      paymentCampaigns,
      packageGroups,
      collections,
      title,
      localization,
      isLoading: false,
    };
  },
  watch: {
    form(form, oldForm) {
      if (this.form.imageFile !== oldForm.imageFile) {
        this.imageUrl = URL.createObjectURL(this.form.imageFile);
      }

      if (this.form.thumbnailAdFile !== oldForm.thumbnailAdFile) {
        this.thumbnailAdUrl = this.form.thumbnailAdFile
          ? URL.createObjectURL(this.form.thumbnailAdFile)
          : null;
      }

      if (this.form.thumbnailFile !== oldForm.thumbnailFile) {
        this.thumbnailUrl = URL.createObjectURL(this.form.thumbnailFile);
      }

      if (!isNil(this.form.registrationForm)) {
        this.form.registrationForm.sections.forEach((_section, si) => {
          const section = this.form.registrationForm.sections[si];
          section.inputs.forEach((_input, ii) => {
            const formInput = section.inputs[ii];
            formInput.watermarkUrl = !isNil(formInput.watermarkFile)
              ? URL.createObjectURL(formInput.watermarkFile)
              : formInput.watermarkUrl;
          });
        });
      }

      if (this.form.services !== oldForm.services) {
        this.createPackageOptionPrice();
      }
    },
  },
  created() {
    this.setup();
  },
  methods: {
    setup() {
      this.setupData();
    },
    async setupData() {
      await Promise.all([
        this.setupTargets(),
        this.setupSellerTradeParties(),
        this.setupPaymentCampaigns(),
        this.setupPackageGroups(),
        this.setupCollections(),
        this.setupForm(),
      ]);
    },
    async setupTargets() {
      const targets = await this.fetchTargets();

      this.targets = targets;
    },
    async setupPaymentCampaigns() {
      const paymentCampaigns = await this.fetchPaymentCampaigns();

      this.paymentCampaigns = paymentCampaigns;
    },
    async setupSellerTradeParties() {
      const sellerTradeParties = await this.fetchSellerTradeParties();

      this.sellerTradeParties = sellerTradeParties;
    },
    async setupPackageGroups() {
      const packageGroups = await this.fetchPackageGroups();

      this.packageGroups = packageGroups;
    },
    async setupCollections() {
      const collections = await this.fetchList();

      this.collections = collections;
    },
    async setupForm() {
      if (!this.$route.query.id) {
        return;
      }

      try {
        this.isLoading = true;

        const data = await this.fetchData();

        this.setupPackageForm(data);
      } catch (error) {
        this.$message.error(error.message);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchData() {
      const { id } = this.$route.query;

      const response =
        await baanRueJaiClient.admin.subscriptions.recommend_packages.get(id);

      return response.data.result;
    },
    fetchTargets() {
      return Promise.resolve([
        { id: "0", name: "สำหรับบ้าน" },
        { id: "1", name: "สำหรับคอนโด" },
        { id: "2", name: "สำหรับบ้านขนาดใหญ่" },
      ]);
    },
    async fetchPackageGroups() {
      return baanRueJaiClient.admin.groups.packageGroups.list();
    },
    async fetchList() {
      const response =
        await baanRueJaiClient.admin.subscriptions.collections.list();

      return response.data.result;
    },
    async fetchSellerTradeParties() {
      const response =
        await baanRueJaiClient.admin.subscriptions.receipts.getSellerTradeParties();

      return response.data.result;
    },
    async fetchPaymentCampaigns() {
      const response =
        await baanRueJaiClient.admin.payment.getPaymentCampaigns();

      return response.data.result;
    },
    setupPackageForm(data) {
      const {
        name,
        receipt_name: receiptName,
        detail,
        target,
        price,
        billing_cycle: billingCycle,
        terms_and_conditions: termsAndConditions,
        seller_trade_party_id: sellerTradePartyId,
        image_url: imageUrl,
        services,
        projects,
        homes,
        thumbnail_url: thumbnailUrl,
        is_secret: isSecret,
        is_expire_after_appointment: isExpireAfterAppointment,
        note_label: noteLabel,
        registration_form: registrationFormData,
        subpackages,
        purchasing_limit: purchasingLimit,
        purchase_success_title: purchaseSuccessTitle,
        purchase_success_message: purchaseSuccessMessage = null,
        expired_at: expiredAt,
        package_albums: packageAlbums,
        has_advance_notice: hasAdvanceNotice,
        advance_notice_message: advanceNoticeMessage,
        advance_notice_days: advanceNoticeDuration,
        is_ignore_count_coupon: isIgnoreCountCoupon,
        payment_campaign_ids: paymentCampaignIds,
        is_required_issue_receipt: isRequiredIssueReceipt,
        thumbnail_ad: thumbnailAdUrl,
        detail_images: detailImageAlbums,
        detail_images_position: detailImagesPosition,
        coin_amount_percent: coinAmountPercent,
        coin_amount_fix: coinAmountFix,
        group_ids: packageGroupIds,
        label_ids: labelIds,
        collection_ids: collectionIds,
        is_available_for_sale: isAvailableForSale,
        is_visible_on_app: isVisibleOnApp,
      } = data;

      let coinAmountStatus = "no";
      if (coinAmountPercent) {
        coinAmountStatus = "percent";
      } else if (coinAmountFix) {
        coinAmountStatus = "fix";
      }

      this.form = {
        ...this.form,
        name,
        receiptName,
        detail,
        target,
        price: convertBathToStangAndRoundTwoDecimal(price),
        billingCycle,
        termsAndConditions,
        sellerTradePartyId,
        services: (services || []).map((service) => {
          const {
            id,
            name: serviceName,
            short_description: shortDescription,
            description,
            icon_url: iconUrl,
            options,
            quota,
          } = service;

          return {
            id,
            iconUrl,
            name: serviceName,
            shortDescription,
            description,
            provider: {
              ...service.provider,
              logoUrl: service.provider.logo_url,
            },
            options,
            quota,
          };
        }),
        projects: (projects || []).map((project) => ({
          ...project,
          projectId: project.project_id,
        })),
        homes: (homes || []).map((home) => ({
          ...home,
        })),
        packageSelectorType:
          (projects || []).length === 0 && (homes || []).length > 0
            ? "perUnit"
            : "project",
        imageFile: null,
        thumbnailFile: null,
        isSecret,
        isExpireAfterAppointment,
        noteLabel,
        registrationForm: toCamelCaseObject(registrationFormData),
        hasRegistrationForm: !!registrationFormData,
        packageCollection: !isEmpty(subpackages)
          ? subpackages.map((item) => item.id).toString()
          : null,
        purchasingLimit,
        purchaseSuccessTitle,
        purchaseSuccessMessage,
        expiredAt: toOptionalDateTime(expiredAt),
        packageAlbums,
        hasAdvanceNotice,
        advanceNoticeMessage,
        advanceNoticeDuration,
        isIgnoreCountCoupon,
        paymentCampaignIds,
        isRequiredIssueReceipt,
        thumbnailAdFile: null,
        detailImagesPosition,
        detailImageAlbums,
        coinAmountPercent,
        coinAmountFix,
        coinAmountStatus,
        packageGroupIds: packageGroupIds || [],
        labelIds: labelIds || [],
        collectionIds: collectionIds || [],
      };
      this.imageUrl = imageUrl;
      this.thumbnailUrl = thumbnailUrl;
      this.thumbnailAdUrl = thumbnailAdUrl;
      this.isAvailableForSale = isNil(isAvailableForSale)
        ? false
        : isAvailableForSale;
      this.isVisibleOnApp = isNil(isVisibleOnApp) ? false : isVisibleOnApp;
    },
    async handleClickSave() {
      try {
        await this.$refs.packageEditor.validate();
      } catch (invalidFields) {
        const messages = Object.values(invalidFields || {})
          .map((fieldErrors) => fieldErrors[0] && fieldErrors[0].message)
          .filter(Boolean);

        await this.$alert(
          messages.length
            ? messages.join("<br>")
            : "โปรดตรวจสอบข้อมูลให้ครบถ้วน",
          "แจ้งเตือน",
          {
            confirmButtonText: "ปิด",
            type: "warning",
            dangerouslyUseHTMLString: true,
          }
        );
        return;
      }

      if (this.isOriginalPriceLessThanOrEqualPrice()) {
        this.$message.error(
          "ไม่สามารถบันทึกได้เนื่องจากราคาเต็มน้อยกว่าหรือเท่ากับราคาที่ลดแล้ว"
        );
        return;
      }

      if (this.form.price < 0) {
        this.$message.error("ไม่สามารถบันทึกได้เนื่องจากใส่ราคาไม่ถูกต้อง");
        return;
      }

      if (!isNil(this.form.price.toString().split(".")[1])) {
        if (this.form.price.toString().split(".")[1].length > 2) {
          this.$message.error(
            "ไม่สามารถบันทึกได้เนื่องจากราคามีทศนิยมมากกว่า 2 ตำแหน่ง"
          );
          return;
        }
      }

      if (this.form.billingCycle < 0) {
        this.$message.error("ไม่สามารถบันทึกได้เนื่องจากใส่รอบตัดบิลไม่ถูกต้อง");
        return;
      }

      if (this.form.maximumQuantity < 1) {
        this.$message.error("กรุณาใส่จำนวนที่ซื้อได้สูงสุด");
        return;
      }

      if (this.form.hasAdvanceNotice === true) {
        if (
          this.form.advanceNoticeDuration > 0 &&
          (isEmpty(this.form.advanceNoticeMessage) ||
            isNil(this.form.advanceNoticeMessage))
        ) {
          this.$message.error("กรุณาใส่ขอความแจ้งเตือน");
          return;
        }

        if (this.form.advanceNoticeDuration < 1) {
          this.$message.error("กรุณาใส่จำนวนวันแจ้งเตือนล่วงหน้า");
          return;
        }
      }

      if (isNil(this.form.sellerTradePartyId)) {
        this.$message.error("ไม่สามารถบันทึกได้เนื่องจากยังไม่ได้เลือกผู้จัดหาบริการ");
        return;
      }

      if (isEmpty(this.form.services) && isNil(this.form.packageCollection)) {
        this.$message.error("กรุณาใส่รายการบริการ");
        return;
      }

      if (
        this.isEmptyServicePrice(this.form.packageOptions) ||
        this.isEmptyServiceOptionsPrice(this.form.packageOptions)
      ) {
        this.$message.error("กรุณาใส่ราคาขายรายครั้งให้ครบ");
        return;
      }

      const emptyAppointmentPriceRow = findEmptyAppointmentPriceRow(
        this.form.packageOptions
      );

      if (emptyAppointmentPriceRow) {
        this.$message.error(
          `กรุณาใส่ราคาขายต่อครั้งให้ครบ: ${emptyAppointmentPriceRow}`
        );
        return;
      }

      if (!this.form.packageAlbums || this.form.packageAlbums.length <= 0) {
        this.$message.error("กรุณาใส่รูปรายละเอียด Package");
        return;
      }

      if (!this.form.imageFile && isNil(this.imageUrl)) {
        this.$message.error("กรุณาใส่รูป RJ Recommended");
        return;
      }

      if (!this.form.thumbnailFile && isNil(this.thumbnailUrl)) {
        this.$message.error("กรุณาใส่รูป Thumbnail");
        return;
      }

      if (
        this.form.coinAmountPercent &&
        (this.form.coinAmountPercent < 0 || this.form.coinAmountPercent > 100)
      ) {
        this.$message.error("กรุณาแก้ไขเปอร์เซ็นต์ MNC");

        return;
      }

      if (
        this.form.coinAmountFix &&
        (this.form.coinAmountFix < 0 ||
          convertBathToStangAndRoundTwoDecimal(this.form.coinAmountFix) >
            this.form.price)
      ) {
        this.$message.error("กรุณาแก้ไขการใช้ส่วนลด MNC");

        return;
      }

      try {
        this.isLoading = true;

        await this.createPackage();

        this.$router.push({ name: "PackageListPage" });
      } catch (err) {
        if (
          err.response &&
          err.response.status &&
          err.response.status === 400
        ) {
          this.$message.error(err.response.data.message);
        } else {
          this.$message.error(err.message);
        }
      } finally {
        this.isLoading = false;
      }
    },
    handleSetupPricings() {
      if (this.form.hasOptionPackage && !isEmpty(this.form.services)) {
        const options = this.getOptions(this.form.services);

        this.setupPricings(options);
      } else {
        this.form.pricings = [];
      }
    },
    getOptions(services) {
      const options = !isEmpty(services)
        ? services.flatMap((service) =>
            service.options.map((option) =>
              option.variants.map((variant) => ({
                service: service.name,
                option: option.title,
                variant: variant.title,
                isSale: variant.is_sale,
              }))
            )
          )
        : [];

      return options;
    },
    setupPricings(options) {
      const pricingPatterns = this.createPricingPatterns(options);

      this.form.pricings = !isEmpty(pricingPatterns)
        ? pricingPatterns.map((pattern) => ({
            variants: pattern,
            isSell: true,
            isSale: false,
            originalPrice: null,
            price: 0,
          }))
        : [];
    },
    createPricingPatterns(options) {
      if (isEmpty(options)) {
        return [];
      }

      if (options.length === 1) {
        return head(options).map((subOption) => [subOption]);
      }

      const firstOption = head(options);
      const otherOptions = tail(options);
      const patternOptions = this.createPricingPatterns(
        otherOptions
      ).flatMap((subOtherOption) =>
        firstOption.map((subOption) => [subOption, ...subOtherOption])
      );

      return patternOptions;
    },
    async createPackage() {
      const {
        name,
        receiptName,
        target,
        price,
        pricings,
        billingCycle,
        detail,
        termsAndConditions,
        sellerTradePartyId,
        services,
        projects,
        homes,
        imageFile,
        thumbnailFile,
        isSecret,
        isExpireAfterAppointment,
        noteLabel,
        registrationForm,
        packageCollection,
        purchasingLimit,
        originalPrice,
        purchaseSuccessTitle,
        purchaseSuccessMessage,
        expiredAt,
        packageOptions,
        minimumQuantity,
        maximumQuantity,
        packageAlbums,
        hasAdvanceNotice,
        advanceNoticeMessage,
        advanceNoticeDuration,
        isIgnoreCountCoupon,
        paymentCampaignIds,
        isRequiredIssueReceipt,
        thumbnailAdFile,
        labelIds,
        detailImagesPosition,
        detailImageAlbums,
        coinAmountPercent,
        coinAmountFix,
        packageGroupIds,
        collectionIds,
      } = this.form;

      const newPackageOptions = await this.setupPackageOptionsMultiplyPrice(
        packageOptions
      );

      const isOneTimePackage = billingCycle === 0;

      const subpackageIds = packageCollection
        ? packageCollection.split(",").map((id) => id.trim())
        : [];
      const image = imageFile
        ? await uploadPublicImage(imageFile)
        : this.imageUrl;
      const thumbnail = thumbnailFile
        ? await uploadPublicImage(thumbnailFile)
        : this.thumbnailUrl;
      const thumbnailAd = thumbnailAdFile
        ? await uploadPublicImage(thumbnailAdFile)
        : this.thumbnailAdUrl;
      const serviceIds = (services || []).map(({ id }) => id.toString());
      const projectIds = projects.map(({ projectId }) => projectId);
      const homeIds = homes.map(({ home_id: homeId }) => homeId);
      // PROBLEM : too many async await and Promise.all
      const registrationFormPayload = registrationForm
        ? {
            ...registrationForm,
            sections: await Promise.all(
              registrationForm.sections.map(async (section) => ({
                  ...section,
                  inputs: await Promise.all(
                    section.inputs.map(async (input) => ({
                        isRequired: input.isRequired,
                        prefillKey: input.prefillKey,
                        title: input.title,
                        type: input.type,
                        watermarkUrl: input.watermarkFile
                          ? await uploadPublicImage(
                              input.watermarkFile,
                              name,
                              "article"
                            )
                          : null
                      }))
                  )
                }))
            )
          }
        : null;

      const params = {
        name,
        receiptName,
        target,
        price: convertStangToBathAndRoundTwoDecimal(price),
        pricings,
        billingCycle: isOneTimePackage ? 0 : billingCycle,
        detail,
        termsAndConditions,
        sellerTradePartyId,
        serviceIds,
        projectIds,
        homeIds,
        imageUrl: image,
        thumbnailUrl: thumbnail,
        isSecret,
        isExpireAfterAppointment,
        noteLabel,
        registrationForm: registrationFormPayload,
        subpackageIds,
        purchasingLimit,
        originalPrice:
          originalPrice !== null
            ? convertStangToBathAndRoundTwoDecimal(originalPrice)
            : null,
        purchaseSuccessTitle,
        purchaseSuccessMessage,
        expiredAt: toOptionalISOString(expiredAt),
        packageOptions: newPackageOptions,
        minimumQuantity: parseInt(minimumQuantity, 10),
        maximumQuantity: parseInt(maximumQuantity, 10),
        packageAlbums,
        hasAdvanceNotice,
        advanceNoticeMessage,
        advanceNoticeDuration: parseInt(advanceNoticeDuration, 10),
        isIgnoreCountCoupon,
        paymentCampaignIds,
        isRequiredIssueReceipt,
        thumbnailAd,
        labelIds,
        detailImages: !isEmpty(detailImageAlbums) ? detailImageAlbums : null,
        detailImagesPosition,
        coinAmountPercent,
        coinAmountFix,
        packageGroups: packageGroupIds,
        collectionIds,
        isAvailableForSale: this.isAvailableForSale,
        isVisibleOnApp: this.isVisibleOnApp,
      };

      await backendAdminClient.subscriptions.createPackage(
        params
      );
    },
    isOriginalPriceLessThanOrEqualPrice() {
      return (
        this.form.originalPrice !== null &&
        this.form.originalPrice <= this.form.price
      );
    },
    createPackageOptionPrice() {
      const packageOptions = this.form.services.map((serviceDetail) => {
        const serviceId = serviceDetail.id;
        const serviceTitle = serviceDetail.name;
        const isAppointment = serviceDetail.quota > 0;
        let serviceMainOption = null;
        let serviceExtraOption = null;
        let serviceOptionVariants = null;
        let serviceAppointmentPrices = null;

        if (serviceDetail.options && serviceDetail.options.service_options) {
          serviceDetail.options.service_options.forEach((serviceOption) => {
            if (serviceOption.extra_option === true) {
              serviceExtraOption = {
                id: serviceOption.id,
                title: serviceOption.title,
              };
            } else {
              serviceMainOption = {
                id: serviceOption.id,
                title: serviceOption.title,
              };
            }
          });

          serviceOptionVariants =
            serviceDetail.options.service_option_costs.map(
              (serviceOptionCost) => {
                const variants = serviceOptionCost.combination_costs.map(
                  (combineCost, combineCostIndex) => {
                    const extraOption = combineCostIndex !== 0;

                    const appointmentPrices = combineCost.appointment_costs.map(
                      (appointmentCost) => ({
                          appoint_no: appointmentCost.appoint_no,
                          appoint_price: null,
                        })
                    );

                    return {
                      id: combineCost.id,
                      title: combineCost.title,
                      extra_option: extraOption,
                      price: null,
                      original_price: null,
                      is_out_of_stock: combineCost.is_out_of_stock,
                      is_discount: false,
                      is_not_sale: combineCost.is_not_sale,
                      appointment_prices: appointmentPrices,
                    };
                  }
                );

                return {
                  id: serviceOptionCost.id,
                  title: serviceOptionCost.title,
                  variants,
                };
              }
            );
        } else {
          const qouta = serviceDetail.quota > 0 ? serviceDetail.quota : 1;

          const fillAppointment = Array.from({ length: qouta }).fill();

          serviceAppointmentPrices = fillAppointment.map((item, index) => ({
              appoint_no: index + 1,
              appoint_price: null,
            }));
        }

        return {
          service_id: serviceId,
          service_title: serviceTitle,
          service_main_option: serviceMainOption,
          service_extra_option: serviceExtraOption,
          service_option_variants: serviceOptionVariants,
          service_price: null,
          service_original_price: null,
          is_out_of_stock: false,
          is_discount: false,
          is_not_sale: false,
          service_appointment_prices: serviceAppointmentPrices,
          is_appointment: isAppointment,
        };
      });

      this.form.packageOptions = packageOptions;
    },
    isEmptyServiceOptionsPrice(packageOptions) {
      const isOptionPrice = packageOptions.some((packageOption) => {
        const isPrice = packageOption.service_option_variants
          ? packageOption.service_option_variants.some((optionVariant) => optionVariant.variants.some((variant) => !variant.price))
          : false;

        return isPrice;
      });

      return isOptionPrice;
    },
    isEmptyServicePrice(packageOptions) {
      const hasEmptyPrice = packageOptions
        ? packageOptions.some(
            (packageOption) =>
              !packageOption.service_option_variants &&
              !packageOption.service_price
          )
        : false;

      return hasEmptyPrice;
    },
    async setupPackageOptionsMultiplyPrice(packageOptionsDetail) {
      if (!packageOptionsDetail) return undefined;

      const packageOptions = packageOptionsDetail.map((packageOption) => {
        let serviceOptionVariants;
        let serviceAppointmentPrices;

        const servicePrice = convertStangToBathAndRoundTwoDecimal(
          packageOption.service_price
        );
        const serviceOriginalPrice = convertStangToBathAndRoundTwoDecimal(
          packageOption.service_original_price
        );

        if (
          packageOption.service_appointment_prices &&
          packageOption.service_appointment_prices.length > 0
        ) {
          serviceAppointmentPrices =
            packageOption.service_appointment_prices.map(
              (serviceAppointmentPrice) => {
                const appointPrice = convertStangToBathAndRoundTwoDecimal(
                  serviceAppointmentPrice.appoint_price
                );

                return {
                  appoint_no: serviceAppointmentPrice.appoint_no,
                  appoint_price: appointPrice,
                };
              }
            );
        }

        if (packageOption.service_option_variants) {
          serviceOptionVariants = packageOption.service_option_variants.map(
            (optionVariant) => {
              const variants = optionVariant.variants.map((variant) => {
                const originalPrice = convertStangToBathAndRoundTwoDecimal(
                  variant.original_price
                );
                const price = convertStangToBathAndRoundTwoDecimal(
                  variant.price
                );

                const isEmptyIsNotSaleNewOption = this.newOptionVariants
                  ? this.newOptionVariants.some(
                      (newOptionVariant) => newOptionVariant === variant.id
                    )
                  : false;

                const isNotSale =
                  price >= 0 && isEmptyIsNotSaleNewOption
                    ? false
                    : variant.is_not_sale;

                const appointmentPrices = variant.appointment_prices.map(
                  (appointmentPrice) => {
                    const appointPrice = convertStangToBathAndRoundTwoDecimal(
                      appointmentPrice.appoint_price
                    );

                    return {
                      appoint_no: appointmentPrice.appoint_no,
                      appoint_price: appointPrice,
                    };
                  }
                );

                return {
                  extra_option: variant.extra_option,
                  id: variant.id,
                  is_discount: variant.is_discount,
                  is_not_sale: isNotSale,
                  is_out_of_stock: variant.is_out_of_stock,
                  original_price: originalPrice,
                  price,
                  title: variant.title,
                  appointment_prices: appointmentPrices,
                };
              });

              return {
                id: optionVariant.id,
                title: optionVariant.title,
                variants,
              };
            }
          );
        }

        return {
          is_discount: packageOption.is_discount,
          is_not_sale: packageOption.is_not_sale,
          is_out_of_stock: packageOption.is_out_of_stock,
          service_extra_option: packageOption.service_extra_option,
          service_id: +packageOption.service_id,
          service_main_option: packageOption.service_main_option,
          service_option_variants: serviceOptionVariants,
          service_original_price: serviceOriginalPrice,
          service_price: servicePrice,
          service_title: packageOption.service_title,
          service_appointment_prices: serviceAppointmentPrices,
          is_appointment: packageOption.is_appointment,
        };
      });

      return packageOptions;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.page-create-package {
  display: flex;
  flex-flow: column nowrap;
  flex-grow: 1;
  max-width: 100%;
}

.page-content {
  overflow-y: auto;
  flex-grow: 1;
  padding: 0 30px;
}

.wrapper {
  padding: $space 0;
}

.editor {
  margin-bottom: $space;
}
</style>
